
import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { extractNameFromAnswers } from "@/utils/submissionHelpers";

const CodeRedirect = () => {
  const { code } = useParams<{ code: string }>();
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCodeData = async () => {
      if (!code) {
        setError("Código no proporcionado");
        setIsLoading(false);
        return;
      }

      try {
        // Si es un código de referido (empieza con "R")
        if (code.startsWith('R')) {
          const parentCode = code.substring(1); // R165 -> 165
          
          // Intentar primero V2 (jotform_submissions) - IDs más altos
          // @ts-ignore - Complex Supabase type
          const { data: v2Submission, error: v2Error } = await supabase
            .from('jotform_submissions')
            .select('id, jotform_submission_id, jotform_form_id, email, raw_answers, form_mapping_id')
            .eq('referral_code', parentCode)
            .eq('is_deleted_in_jotform', false)
            .maybeSingle();

          if (v2Submission && !v2Error) {
            // V2: Usar jotform_submissions
            console.log('🔗 V2: Found submission in jotform_submissions');

            // Extraer nombre del padre desde raw_answers usando helper
            const parentName = extractNameFromAnswers(v2Submission.raw_answers) || 'Usuario';

            // Buscar formulario de referido
            const { data: referralFormMapping, error: referralFormError } = await supabase
              .from('jotform_form_mappings')
              .select('jotform_form_id, form_title')
              .eq('parent_form_id', v2Submission.form_mapping_id)
              .eq('is_referral_form', true)
              .single();
              
            if (referralFormError || !referralFormMapping) {
              setError("No existe un formulario de referidos para este formulario");
              setIsLoading(false);
              return;
            }

            // Construir URL de JotForm
            const fullUrl = `https://form.jotform.com/${referralFormMapping.jotform_form_id}?codigo=${code}&codigoTitulo=${encodeURIComponent(parentName)}`;
            setRedirectUrl(fullUrl);
            setIsLoading(false);
            return;
          }

          // Si no se encontró, mostrar error
          setError("La respuesta de referencia no existe");
          setIsLoading(false);
          return;
        }

        // Para códigos normales, usar la lógica existente
        // First query to get the form_launch_id AND title associated with the code
        const { data: codeData, error: codeError } = await supabase
          .from("form_codes")
          .select("form_launch_id, title")
          .eq("code", code)
          .maybeSingle();  // Changed from single() to maybeSingle()

        if (codeError || !codeData) {
          console.error("Error al buscar el código:", codeError);
          setError("Código no válido");
          setIsLoading(false);
          return;
        }

        // Get the form URL using V2 query (jotform_form_mappings)
        const { data: launchData, error: launchError } = await supabase
          .from("form_launches")
          .select(`
            id,
            form_mapping_id,
            jotform_form_mappings (jotform_form_id)
          `)
          .eq("id", codeData.form_launch_id)
          .maybeSingle();

        if (launchError || !launchData) {
          console.error("Error al buscar el lanzamiento:", launchError);
          setError("No se encontró información del formulario");
          setIsLoading(false);
          return;
        }

        let formUrl = null;

        // Use jotform_form_mappings to get JotForm URL
        if (launchData.form_mapping_id && launchData.jotform_form_mappings?.jotform_form_id) {
          const jotformId = launchData.jotform_form_mappings.jotform_form_id;
          formUrl = `https://form.jotform.com/${jotformId}`;
        } else {
          console.error("El lanzamiento no tiene un formulario asignado");
          setError("Este lanzamiento no tiene un formulario configurado");
          setIsLoading(false);
          return;
        }

        if (!formUrl) {
          setError("No se pudo determinar la URL del formulario");
          setIsLoading(false);
          return;
        }

        // Get the title and provide a fallback if it's null
        const codeTitle = codeData.title || code;

        // Construct the full URL with code and title
        const fullUrl = `${formUrl}?codigo=${code}&codigoTitulo=${encodeURIComponent(codeTitle)}`;
        setRedirectUrl(fullUrl);
        setIsLoading(false);
      } catch (err) {
        console.error("Error al procesar el código:", err);
        setError("Error al procesar el código");
        setIsLoading(false);
      }
    };

    fetchCodeData();
  }, [code]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Cargando formulario...</h2>
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="bg-red-50 p-6 rounded-lg border border-red-200 max-w-md text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">Error</h2>
          <p className="text-gray-700">{error}</p>
          <p className="mt-4 text-sm text-gray-500">
            El código proporcionado no es válido o ha expirado.
          </p>
        </div>
      </div>
    );
  }

  if (redirectUrl) {
    // Using window.location instead of Navigate to do a full redirect to external URL
    window.location.href = redirectUrl;
    return null;
  }

  return null;
};

export default CodeRedirect;
